import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { ASSET_TYPE_ICONS } from '@/lib/assetTypes';
import { FileText } from 'lucide-react';

/**
 * AssetTypeCard — Step 1 of CreateAssessmentDialog.
 * Renders a single asset-type radio card with icon, name, description, and
 * capability hints sourced from the backend meta.
 */
export function AssetTypeCard({ asset, selected, onSelect }) {
  const { t, i18n } = useTranslation();
  const label = i18n.language?.startsWith('es') ? asset.label_es : asset.label_en;
  const Icon = ASSET_TYPE_ICONS[asset.code] || FileText;

  // Build a short "best for" capability hint from capabilities map.
  const hints = [];
  const caps = asset.capabilities || {};
  if (caps.likert_scales === 'on') hints.push(t('studio.assetCard.hints.likert', { defaultValue: 'Likert scale' }));
  if (caps.correct_answers === 'on') hints.push(t('studio.assetCard.hints.correctAnswers', { defaultValue: 'Correct answers' }));
  if (caps.passing_score === 'required') hints.push(t('studio.assetCard.hints.passingScore', { defaultValue: 'Passing score' }));
  if (caps.climate_dimensions === 'on') hints.push(t('studio.assetCard.hints.climate', { defaultValue: 'Climate dimensions' }));
  if (caps.certification_metadata === 'on') hints.push(t('studio.assetCard.hints.certification', { defaultValue: 'Certification' }));
  if (caps.competencies === 'required') hints.push(t('studio.assetCard.hints.competencies', { defaultValue: 'Competencies' }));

  const reportingTargetMap = {
    talentcheck: 'TalentCheck',
    climara: 'Climara',
    talentcompetency: 'TalentCompetency',
    talenttrain: 'TalentTrain',
    talentmap: 'TalentMap',
  };
  const reportTarget = reportingTargetMap[asset.reporting_target];

  return (
    <button
      type="button"
      onClick={() => onSelect(asset.code)}
      data-testid={`studio-asset-card-${asset.code}`}
      className="text-left w-full"
    >
      <Card
        className={cn(
          'p-4 h-full cursor-pointer transition-all border-2',
          selected
            ? 'border-primary ring-2 ring-primary/30 shadow-md'
            : 'border-border hover:border-primary/40 hover:shadow-sm',
        )}
      >
        <div className="flex items-start gap-3">
          <div className={cn(
            'rounded-lg p-2.5 shrink-0',
            selected ? 'bg-primary/15 text-primary' : 'bg-muted text-foreground',
          )}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-sm leading-snug">{label}</h3>
            <p className="text-xs text-muted-foreground mt-1">
              {reportTarget
                ? t('studio.assetCard.reportsTo', { suite: reportTarget, defaultValue: 'Reports to {{suite}}' })
                : t('studio.assetCard.noSuite', { defaultValue: 'General use — no suite report' })}
            </p>
            {hints.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {hints.slice(0, 3).map((h) => (
                  <span
                    key={h}
                    className="text-xs rounded px-1.5 py-0.5 bg-muted/80 text-muted-foreground"
                  >
                    {h}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>
    </button>
  );
}

export default AssetTypeCard;
